import { Component } from '@theme/component';

/**
 * Page-level event keeping every plan selector on the same plan.
 *
 * The PDP carries the selector twice — in the buy box and again in the pricing
 * section — and the spec requires clicking either to move the other. They are
 * separate forms with separate radio groups, so nothing in the DOM couples them.
 */
const PLAN_SELECT_EVENT = 'rytma:plan-select';

/**
 * One source of truth for the selected plan.
 *
 * Every price string on the page — the price row, the strike-through, the save
 * badge, the pill inside the Add to Cart bar — is read from the selected plan's
 * own dataset rather than duplicated in Liquid. There is exactly one place a
 * price can be wrong.
 *
 * The hidden `id` input is what Horizon's <product-form-component> POSTs, so
 * selecting a plan and adding to cart cannot disagree.
 */
class RytmaPlansComponent extends Component {
  #abortController = new AbortController();

  /** Set while applying a selection broadcast by another instance. */
  #syncing = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(PLAN_SELECT_EVENT, this.#onRemoteSelect, {
      signal: this.#abortController.signal,
    });

    this.#applyInitialSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @returns {HTMLInputElement[]} */
  get #plans() {
    return Array.from(this.querySelectorAll('.rytma-plan__input:not([disabled])'));
  }

  /**
   * Radios sharing a name mean the browser keeps the *last* `checked` one it
   * parsed. The schema says the first plan marked default wins, so normalize
   * here rather than depending on markup order.
   */
  #applyInitialSelection() {
    const plans = this.#plans;
    if (!plans.length) return;

    const selected = plans.find((p) => p.dataset.preselected === 'true') ?? plans[0];
    selected.checked = true;
    this.#apply(selected);
  }

  handlePlanChange() {
    const plan = this.querySelector('.rytma-plan__input:checked');
    if (!(plan instanceof HTMLInputElement) || plan.disabled) return;

    this.#apply(plan);

    if (this.#syncing) return;

    const planId = plan.dataset.planId;
    if (!planId) return;

    document.dispatchEvent(
      new CustomEvent(PLAN_SELECT_EVENT, { detail: { planId, source: this } })
    );
  }

  /**
   * Mirrors another selector's choice. Matches on the plan id rather than the
   * index so the two stay in step even if one is configured with a different
   * number of plans or a different order.
   *
   * @param {Event} event
   */
  #onRemoteSelect = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const { planId, source } = event.detail ?? {};
    if (source === this || !planId) return;

    const match = this.#plans.find((p) => p.dataset.planId === planId);
    if (!match || match.checked) return;

    this.#syncing = true;
    match.checked = true;
    this.#apply(match);
    this.#syncing = false;
  };

  /** @param {HTMLInputElement} plan */
  #apply(plan) {
    const d = plan.dataset;

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && d.variantId) {
      variantInput.value = d.variantId;
    }

    // Mirror every derived price string. Missing targets are fine — the pricing
    // section has no ATC pill, the buy box has no per-card footer.
    this.#text('price', d.price);
    this.#text('compare', d.compare);
    this.#text('save', d.save);
    this.#text('atcPrice', d.price);
    this.#text('atcCompare', d.compare);

    // Hide the strike-through entirely when a plan has no compare-at price,
    // rather than leaving an empty struck span behind.
    const compare = this.refs.compare;
    if (compare instanceof HTMLElement) compare.hidden = !d.compare;
    const save = this.refs.save;
    if (save instanceof HTMLElement) save.hidden = !d.save;

    this.#applyLadder(Number(d.tier) || 1);
    this.#applyCta(d);
  }

  /**
   * Marks each ladder slot earned or locked for the given tier, and updates the
   * "N of 3 unlocked" label on the box border.
   *
   * A slot is earned when the selected tier is at or above the tier it unlocks
   * at, so the ladder is derived from one number rather than from a second copy
   * of the tier-to-reward mapping.
   *
   * @param {number} tier
   */
  #applyLadder(tier) {
    const slots = this.querySelectorAll('[data-unlock-at]');
    if (!slots.length) return;

    let earned = 0;
    for (const slot of slots) {
      const at = Number(slot.getAttribute('data-unlock-at')) || 1;
      const isEarned = tier >= at;
      if (isEarned) earned += 1;
      slot.setAttribute('data-earned', String(isEarned));
    }

    // querySelector, not refs. component.js scopes a ref to the *nearest*
    // enclosing Component (see its #isDescendant), and the ladder sits inside
    // the nested <product-form-component>, so refs on it belong to that form
    // and are invisible here. Same reason the variant input is read this way.
    const label = this.querySelector('[data-ladder-label]');
    if (label instanceof HTMLElement) {
      label.textContent = `${earned} of ${slots.length} unlocked`;
    }
  }

  /**
   * Rewrites the Add to Cart label so the button restates the saving, and
   * appends the guarantee for that tier.
   *
   * The two spans are rendered inside Horizon's add-to-cart-button snippet,
   * which echoes its label argument unescaped — so they survive as real refs and
   * the button never has to be re-rendered to change.
   *
   * @param {DOMStringMap} d
   */
  #applyCta(d) {
    // Also inside the product form, so also queried rather than ref'd.
    const label = this.querySelector('[data-cta-label]');
    if (label instanceof HTMLElement && d.cta) label.textContent = d.cta;

    const guarantee = this.querySelector('[data-cta-guarantee]');
    if (guarantee instanceof HTMLElement) {
      guarantee.textContent = d.guarantee ? ` · ${d.guarantee}` : '';
    }
  }

  /**
   * @param {string} ref
   * @param {string | undefined} value
   */
  #text(ref, value) {
    const el = this.refs[ref];
    if (el instanceof HTMLElement && value != null) el.textContent = value;
  }
}

/**
 * Slot-based product gallery.
 *
 * Not Horizon's media-gallery: that renders `product.media`, and this design
 * assigns each of its six frames from a named image slot instead, so there is
 * no product-media list to drive it. Kept deliberately small — the thumbnail
 * swaps the stage image and nothing else.
 */
class RytmaGalleryComponent extends Component {
  requiredRefs = ['stage'];

  /**
   * The `on:click` attribute lives on each thumbnail button, not on the list.
   *
   * That placement is load-bearing. `component.js` proxies the event so
   * `event.target` returns whichever element carries the attribute — bind it to
   * the list and every click reports the <ul>, whose `closest('[data-full]')`
   * is null, so the handler returns early and the stage never changes. Bound to
   * the button, a click on the inner <img> still resolves to the button.
   */
  handleThumbClick(event) {
    const thumb = event.target instanceof Element ? event.target.closest('[data-full]') : null;
    if (!(thumb instanceof HTMLElement)) return;

    const { full, alt } = thumb.dataset;
    const stage = this.refs.stage;
    const img = stage instanceof HTMLElement ? stage.querySelector('img') : null;

    if (img instanceof HTMLImageElement && full) {
      img.src = full;
      img.srcset = '';
      if (alt) img.alt = alt;
    }

    for (const other of this.querySelectorAll('[data-full]')) {
      other.setAttribute('aria-current', String(other === thumb));
    }
  }
}

if (!customElements.get('rytma-plans-component')) {
  customElements.define('rytma-plans-component', RytmaPlansComponent);
}

if (!customElements.get('rytma-gallery-component')) {
  customElements.define('rytma-gallery-component', RytmaGalleryComponent);
}
